class RecentlyViewedSection {
  static EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

  constructor() {
    this.section = document.querySelector('[data-recently-viewed-section]');
    this.grid = document.getElementById('recently-viewed-grid');
    this.loadingOverlay = this.section?.querySelector('.loading-overlay');
    this.recentlyViewedEntries = [];
    this.recentlyViewedProductIdSet = new Set();
    this.badgeObserver = null;

    this.init();
  }

  async init() {
    const entries = this.getRecentlyViewedEntries();

    if (entries.length === 0) {
      this.hideSection();
      return;
    }

    const enrichedEntries = await this.enrichEntriesWithProductIds(entries);

    if (enrichedEntries.length === 0) {
      this.hideSection();
      return;
    }

    if (this.section && this.grid) {
      this.showSection();
      await this.loadProducts(enrichedEntries);
    }

    this.updateProductBadges();
  }

  getRecentlyViewedEntries() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy &&
          !window.Shopify.customerPrivacy.preferencesProcessingAllowed()) {
        this.setRecentlyViewedEntries([]);
        return [];
      }

      const raw = JSON.parse(localStorage.getItem('recently_viewed_products') || '[]');
      const now = Date.now();
      const normalized = [];
      let changed = false;

      if (Array.isArray(raw)) {
        for (const item of raw) {
          let handle = null;
          let timestamp = now;
          let productId;

          if (typeof item === 'string') {
            handle = item;
            changed = true;
          } else if (item && typeof item.handle === 'string') {
            handle = item.handle;
            const parsedTimestamp = this.parseTimestamp(item.timestamp);
            if (parsedTimestamp !== null) {
              timestamp = parsedTimestamp;
            } else {
              changed = true;
            }

            if (item.productId !== undefined && item.productId !== null) {
              const parsedProductId = this.parseProductId(item.productId);
              if (parsedProductId) {
                productId = parsedProductId;
              } else {
                changed = true;
              }
            }
          } else {
            changed = true;
          }

          if (!handle) {
            continue;
          }

          if (now - timestamp > RecentlyViewedSection.EXPIRY_MS) {
            changed = true;
            continue;
          }

          const entry = { handle, timestamp };
          if (productId) {
            entry.productId = productId;
          }
          normalized.push(entry);
        }
      }

      this.setRecentlyViewedEntries(normalized, changed);

      return this.recentlyViewedEntries;
    } catch (error) {
      console.error('Recently Viewed Section: Error getting products:', error);
      this.setRecentlyViewedEntries([]);
      return [];
    }
  }

  async enrichEntriesWithProductIds(entries) {
    if (!Array.isArray(entries) || entries.length === 0) {
      this.setRecentlyViewedEntries([]);
      return [];
    }

    const enrichedEntries = [];
    let changed = false;

    for (const entry of entries) {
      if (!entry || typeof entry.handle !== 'string') {
        changed = true;
        continue;
      }

      const resolvedEntry = await this.resolveEntryWithProductId(entry);

      if (!resolvedEntry) {
        changed = true;
        continue;
      }

      if (
        entry.productId === undefined ||
        entry.productId === null ||
        this.parseProductId(entry.productId) !== resolvedEntry.productId
      ) {
        changed = true;
      }

      enrichedEntries.push(resolvedEntry);
    }

    this.setRecentlyViewedEntries(enrichedEntries, changed);

    return enrichedEntries;
  }

  async resolveEntryWithProductId(entry) {
    const normalizedEntry = {
      handle: entry.handle,
      timestamp: entry.timestamp,
    };

    if (entry.productId !== undefined && entry.productId !== null) {
      const parsed = this.parseProductId(entry.productId);
      if (parsed) {
        normalizedEntry.productId = parsed;
        return normalizedEntry;
      }
    }

    const productData = await this.fetchProductJson(entry.handle);

    if (!productData || !productData.id) {
      return null;
    }

    normalizedEntry.productId = productData.id.toString();

    return normalizedEntry;
  }

  async fetchProductJson(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Recently Viewed Section: Error fetching product data:', error);
      return null;
    }
  }

  async loadProducts(entries) {
    if (!this.grid) {
      return;
    }

    const productHandles = entries.map((entry) => entry.handle);

    if (productHandles.length === 0) {
      this.hideSection();
      return;
    }

    try {
      this.showLoading();

      const searchQuery = productHandles.map((handle) => `handle:${handle}`).join(' OR ');

      const url = new URL('/search', window.location.origin);
      url.searchParams.set('q', searchQuery);
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[prefix]', 'last');

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productCards = doc.querySelectorAll('#search-grid .grid__item');

      this.grid.innerHTML = '';

      const orderedProducts = [];
      for (const handle of productHandles) {
        const matchingCard = Array.from(productCards).find((card) => {
          const link = card.querySelector('a[href*="/products/"]');
          return link && link.href.includes(`/products/${handle}`);
        });
        if (matchingCard) {
          const listItem = document.createElement('li');
          listItem.className = 'grid__item';
          if (window.settings && window.settings.animations_reveal_on_scroll) {
            listItem.classList.add('scroll-trigger', 'animate--slide-in');
            listItem.style.setProperty('--animation-order', orderedProducts.length + 1);
          }

          listItem.innerHTML = matchingCard.innerHTML;
          orderedProducts.push(listItem);
        }
      }

      orderedProducts.forEach((element) => {
        this.grid.appendChild(element);
      });

      this.hideLoading();

      if (orderedProducts.length === 0) {
        this.hideSection();
      }
    } catch (error) {
      console.error('Recently Viewed Section: Error loading products:', error);
      this.hideLoading();
      this.hideSection();
    }
  }

  showSection() {
    this.section?.classList.remove('recently-viewed--empty');
  }

  hideSection() {
    this.section?.classList.add('recently-viewed--empty');
  }

  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = 'block';
    }
  }

  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = 'none';
    }
  }

  updateProductBadges() {
    if (!this.recentlyViewedProductIdSet.size) {
      this.resetRecentlyViewedBadges();
      this.teardownBadgeObserver();
      return;
    }

    const badgeLabel = this.getRecentlyViewedBadgeLabel();

    if (!badgeLabel) {
      return;
    }

    const badges = document.querySelectorAll('[data-product-badge]');

    badges.forEach((badge) => {
      this.applyRecentlyViewedBadge(badge, badgeLabel);
    });

    this.ensureBadgeObserver();
  }

  setRecentlyViewedEntries(entries, shouldPersist = false) {
    this.recentlyViewedEntries = Array.isArray(entries) ? entries : [];
    this.recentlyViewedProductIdSet = new Set(
      this.recentlyViewedEntries
        .map((entry) => this.parseProductId(entry.productId))
        .filter((id) => !!id)
    );

    if (shouldPersist) {
      localStorage.setItem(
        'recently_viewed_products',
        JSON.stringify(this.recentlyViewedEntries)
      );
    }

    if (this.recentlyViewedProductIdSet.size) {
      this.updateProductBadges();
    } else {
      this.resetRecentlyViewedBadges();
      this.teardownBadgeObserver();
    }
  }

  parseTimestamp(timestamp) {
    if (typeof timestamp === 'number') {
      return timestamp;
    }

    if (typeof timestamp === 'string') {
      const parsed = Number(timestamp);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }

    return null;
  }

  parseProductId(productId) {
    if (typeof productId === 'number') {
      return productId.toString();
    }

    if (typeof productId === 'string') {
      const trimmed = productId.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }

    return null;
  }

  parseBooleanAttribute(value) {
    if (typeof value !== 'string') {
      return false;
    }

    return value.toLowerCase() === 'true';
  }

  getRecentlyViewedBadgeLabel() {
    if (typeof window !== 'undefined') {
      const themeStrings = window.theme?.strings || window.theme?.translations;
      if (themeStrings) {
        const possibleKeys = [
          'recently_viewed_badge',
          'recentlyViewedBadge',
          'recently_viewed_label',
          'recentlyViewedLabel',
        ];

        for (const key of possibleKeys) {
          const value = themeStrings[key];
          if (typeof value === 'string' && value.trim().length > 0) {
            return value.trim();
          }
        }
      }

      if (typeof window.recentlyViewedBadgeLabel === 'string') {
        const trimmed = window.recentlyViewedBadgeLabel.trim();
        if (trimmed.length > 0) {
          return trimmed;
        }
      }
    }

    return 'Recently viewed';
  }

  applyRecentlyViewedBadge(badge, badgeLabel) {
    if (!(badge instanceof Element)) {
      return;
    }

    const productId = badge.getAttribute('data-product-badge-product-id');
    const productIdString = productId ? productId.toString() : '';

    if (!badge.dataset.recentlyViewedOriginalText) {
      badge.dataset.recentlyViewedOriginalText = badge.textContent.trim();
    }

    const isAvailable = this.parseBooleanAttribute(
      badge.getAttribute('data-product-available')
    );
    const isPreOrder = this.parseBooleanAttribute(
      badge.getAttribute('data-product-pre-order')
    );
    const isSale = this.parseBooleanAttribute(
      badge.getAttribute('data-product-sale')
    );

    const qualifies =
      productIdString &&
      this.recentlyViewedProductIdSet.has(productIdString) &&
      isAvailable &&
      !isPreOrder &&
      !isSale;

    if (!qualifies) {
      if (badge.dataset.recentlyViewedApplied === 'true') {
        this.restoreOriginalBadgeText(badge);
      }
      return;
    }

    if (badge.textContent.trim() !== badgeLabel) {
      badge.textContent = '';
      badge.append(document.createTextNode(badgeLabel));
    }

    badge.dataset.recentlyViewedApplied = 'true';
  }

  restoreOriginalBadgeText(badge) {
    const originalText = badge.dataset.recentlyViewedOriginalText ?? '';
    badge.textContent = '';
    if (originalText) {
      badge.append(document.createTextNode(originalText));
    }
    delete badge.dataset.recentlyViewedApplied;
  }

  resetRecentlyViewedBadges() {
    const badges = document.querySelectorAll(
      '[data-product-badge][data-recently-viewed-applied="true"]'
    );

    badges.forEach((badge) => {
      this.restoreOriginalBadgeText(badge);
    });
  }

  ensureBadgeObserver() {
    if (this.badgeObserver || !this.recentlyViewedProductIdSet.size) {
      return;
    }

    this.badgeObserver = new MutationObserver((mutations) => {
      if (!this.recentlyViewedProductIdSet.size) {
        this.teardownBadgeObserver();
        return;
      }

      const badgeLabel = this.getRecentlyViewedBadgeLabel();

      if (!badgeLabel) {
        return;
      }

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            this.processPotentialBadgeNode(node, badgeLabel);
          });
        } else if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          mutation.target.matches('[data-product-badge]')
        ) {
          this.applyRecentlyViewedBadge(mutation.target, badgeLabel);
        }
      });
    });

    this.badgeObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'data-product-badge-product-id',
        'data-product-available',
        'data-product-pre-order',
        'data-product-sale',
      ],
    });
  }

  processPotentialBadgeNode(node, badgeLabel) {
    if (!(node instanceof Element)) {
      return;
    }

    if (node.matches('[data-product-badge]')) {
      this.applyRecentlyViewedBadge(node, badgeLabel);
    }

    const nestedBadges = node.querySelectorAll('[data-product-badge]');
    nestedBadges.forEach((badge) => {
      this.applyRecentlyViewedBadge(badge, badgeLabel);
    });
  }

  teardownBadgeObserver() {
    if (this.badgeObserver) {
      this.badgeObserver.disconnect();
      this.badgeObserver = null;
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new RecentlyViewedSection());
} else {
  new RecentlyViewedSection();
}
